/**
 * ShipmentDiscrepancyPanel.tsx
 *
 * Open discrepancy on a flagged shipment: who reported it, when, the recorded condition and notes,
 * the site's response, and the Sustain-only resolution actions (Ask Site to Recheck, Accept and Reconcile).
 */
import React, { useMemo, useState } from "react";
import { ActivityIndicator, Platform, Pressable, StyleSheet, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { ReceiptBlock, ResolveAction, resolveVariance } from "../../../api/shipmentApi";
import { SITE_OUTCOME_LABELS, computeRecordedVariances } from "../util/shipmentReceiptUtils";
import { formatDateWithTimezone } from "../util/dateFormatter";

export interface ShipmentDiscrepancyPanelProps {
  ticketId: number;
  receipt: ReceiptBlock;
  /** Called with the backend's message after Ask Site to Recheck or Accept and Reconcile succeeds. */
  onResolved: (message: string) => void;
}

const ShipmentDiscrepancyPanel: React.FC<ShipmentDiscrepancyPanelProps> = ({ ticketId, receipt, onResolved }) => {
  const d = receipt.discrepancy;
  const [busy, setBusy] = useState<ResolveAction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const variances = useMemo(
    () => (receipt.skus && !d?.flaggedOnly ? computeRecordedVariances(receipt.skus, d?.unexpectedSerials) : []),
    [receipt.skus, d?.flaggedOnly, d?.unexpectedSerials]
  );

  if (!d) return null;

  const resolve = async (action: ResolveAction) => {
    if (busy) return;
    setError(null);
    setBusy(action);
    const r = await resolveVariance(ticketId, action);
    setBusy(null);
    if (r.error || !r.data) {
      setError(r.error ?? "Failed to resolve the variance.");
      return;
    }
    onResolved(r.data.message);
  };

  return (
    <View style={s.wrap}>
      <View style={s.head}>
        <MaterialIcons name="report-problem" size={16} color="#B42318" />
        <Text style={s.title}>Open discrepancy</Text>
        {d.flaggedOnly ? <View style={s.tag}><Text style={s.tagText}>Flagged without a unit count</Text></View> : null}
      </View>

      <View style={s.grid}>
        <View style={s.kv}><Text style={s.kvLabel}>REPORTED BY</Text><Text style={s.kvValue}>{d.reportedBy ?? "-"}</Text></View>
        <View style={s.kv}><Text style={s.kvLabel}>REPORTED</Text><Text style={s.kvValue}>{formatDateWithTimezone(d.reportedAt)}</Text></View>
        <View style={s.kv}><Text style={s.kvLabel}>CONDITION RECORDED</Text><Text style={s.kvValue}>{d.condition ?? "-"}</Text></View>
      </View>

      {variances.length > 0 ? (
        <View style={s.block}>
          {variances.map((v) => (
            <Text key={v.key} style={s.varianceLine}>
              {v.sku ? <Text style={s.mono}>{v.sku}: </Text> : null}
              {v.detail}
            </Text>
          ))}
        </View>
      ) : null}

      <Text style={s.kvLabel}>NOTES</Text>
      <View style={s.notesBox}><Text style={s.notesText}>{d.notes || "No notes were recorded."}</Text></View>

      {d.recheckRequestedAt ? (
        <Text style={s.hint}>Warehouse Support asked the site to recheck on {formatDateWithTimezone(d.recheckRequestedAt)}.</Text>
      ) : null}

      {d.siteResponse ? (
        <View style={s.response}>
          <Text style={s.kvLabel}>SITE RESPONSE</Text>
          <Text style={s.responseOutcome}>{SITE_OUTCOME_LABELS[d.siteResponse.outcome] ?? d.siteResponse.outcome}</Text>
          <Text style={s.notesText}>{d.siteResponse.text}</Text>
          <Text style={s.hint}>
            {d.siteResponse.respondedBy ?? "Site"} · {formatDateWithTimezone(d.siteResponse.respondedAt)}
          </Text>
        </View>
      ) : (
        <Text style={s.hint}>The site has not responded yet. Warehouse Support cannot reconcile the shipment until it does.</Text>
      )}

      {error ? (
        <View style={s.errorBox}>
          <MaterialIcons name="error-outline" size={16} color="#B42318" />
          <Text style={s.errorText}>{error}</Text>
        </View>
      ) : null}

      {d.canResolve ? (
        <View style={s.actions}>
          <Pressable
            style={({ pressed }) => [s.btnOutline, pressed && s.pressed, !!busy && s.disabled]}
            onPress={() => resolve("RECHECK")}
            disabled={!!busy}
          >
            {busy === "RECHECK" ? <ActivityIndicator size="small" color="#034895" /> : <Text style={s.btnOutlineText}>Ask Site to Recheck</Text>}
          </Pressable>
          <Pressable
            style={({ pressed }) => [s.btnPrimary, pressed && s.pressed, !!busy && s.disabled]}
            onPress={() => resolve("ACCEPT")}
            disabled={!!busy}
          >
            {busy === "ACCEPT" ? <ActivityIndicator size="small" color="#ffffff" /> : <Text style={s.btnPrimaryText}>Accept and Reconcile</Text>}
          </Pressable>
        </View>
      ) : null}
    </View>
  );
};

const MONO = Platform.OS === "ios" ? "Courier" : "monospace";

const s = StyleSheet.create({
  wrap: { margin: 14, marginBottom: 0, padding: 12, borderRadius: 8, borderWidth: 1, borderColor: "#FDA29B", backgroundColor: "#FFFBFA" },
  head: { flexDirection: "row", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" },
  title: { fontSize: 13, fontWeight: "700", color: "#B42318" },
  tag: { backgroundColor: "#FEE4E2", borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 },
  tagText: { fontSize: 10, fontWeight: "700", color: "#B42318" },
  grid: { flexDirection: "row", flexWrap: "wrap", gap: 12, marginBottom: 10 },
  kv: { minWidth: 140, flex: 1 },
  kvLabel: { fontSize: 10, fontWeight: "700", color: "#9ca3af", letterSpacing: 0.5, marginBottom: 3 },
  kvValue: { fontSize: 12, color: "#374151" },
  block: { marginBottom: 10, gap: 2 },
  varianceLine: { fontSize: 12, color: "#7A271A", lineHeight: 18 },
  mono: { fontWeight: "700", fontFamily: MONO },
  notesBox: { backgroundColor: "#ffffff", borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 6, padding: 10, marginTop: 2 },
  notesText: { fontSize: 12, color: "#374151", lineHeight: 18 },
  hint: { fontSize: 11, color: "#6b7280", marginTop: 8, lineHeight: 16 },
  response: { marginTop: 12, padding: 10, borderRadius: 6, backgroundColor: "#F0FDF4", borderWidth: 1, borderColor: "#BBF7D0" },
  responseOutcome: { fontSize: 12, fontWeight: "700", color: "#166534", marginBottom: 4 },
  errorBox: { flexDirection: "row", alignItems: "center", gap: 8, marginTop: 10, backgroundColor: "#FEF3F2", borderWidth: 1, borderColor: "#FDA29B", borderRadius: 8, padding: 10 },
  errorText: { flex: 1, fontSize: 12, color: "#B42318", lineHeight: 17 },
  actions: { flexDirection: "row", justifyContent: "flex-end", flexWrap: "wrap", gap: 10, marginTop: 12 },
  btnOutline: { minHeight: 38, borderWidth: 1.5, borderColor: "#034895", borderRadius: 100, paddingHorizontal: 16, alignItems: "center", justifyContent: "center", backgroundColor: "#ffffff" },
  btnOutlineText: { fontSize: 13, fontWeight: "700", color: "#034895" },
  btnPrimary: { minHeight: 38, borderRadius: 100, paddingHorizontal: 18, alignItems: "center", justifyContent: "center", backgroundColor: "#034895" },
  btnPrimaryText: { fontSize: 13, fontWeight: "700", color: "#ffffff" },
  pressed: { opacity: 0.8 },
  disabled: { opacity: 0.55 },
});

export default ShipmentDiscrepancyPanel;
